/**
 * Story rail — the whole manuscript as one strip of AI-detected beats, in
 * story order, with page turns at the gaps the author toggles. A page is
 * always a contiguous run of beats, so the narrative can never be
 * scrambled; density is a READOUT, not a setting — each page shows how
 * full it is at the book's font size and format, and whatever the text
 * doesn't use is art space.
 */
import { useMemo, useState } from 'react';
import {
  countWords,
  pageCapacityWords,
  pageFillState,
  pagesFromBreaks,
} from '@historyjki/shared';
import type { StoryBeat } from '@historyjki/shared';
import type { ComposeStore } from '../../hooks/useComposeStage';

export function StoryRail({ store }: { store: ComposeStore }) {
  const {
    project,
    beats,
    beatBreaks,
    regrouping,
    updateBeatBreaks,
    spreads,
    selectSpread,
  } = store;
  const [collapsed, setCollapsed] = useState(false);

  const wordCounts = useMemo(
    () => beats.map((b: StoryBeat) => countWords(b.text_content)),
    [beats],
  );
  const capacity = useMemo(
    () => pageCapacityWords(project?.font_scale, project?.format_preset),
    [project?.font_scale, project?.format_preset],
  );
  const groups = useMemo(
    () => pagesFromBreaks(beats.length, beatBreaks),
    [beats.length, beatBreaks],
  );

  if (beats.length === 0) return null;

  const interiors = spreads.filter((s) => s.kind === 'spread');

  const toggleGap = (gap: number) => {
    const next = new Set(beatBreaks);
    if (next.has(gap)) next.delete(gap);
    else next.add(gap);
    void updateBeatBreaks([...next].sort((a, b) => a - b));
  };

  const jumpToPage = (pageIdx: number) => {
    const spread = interiors[Math.floor(pageIdx / 2)];
    if (spread) selectSpread(spread.id);
  };

  return (
    <div
      className={`srail${regrouping ? ' srail--busy' : ''}`}
      data-testid="story-rail"
      aria-busy={regrouping}
    >
      <div className="srail__bar">
        <button
          type="button"
          className="srail__toggle"
          onClick={() => setCollapsed((c) => !c)}
          aria-expanded={!collapsed}
        >
          <span className={`srail__chev${collapsed ? ' srail__chev--closed' : ''}`} aria-hidden>
            ▾
          </span>
          Story rail
        </button>
        <span className="srail__meta">
          {beats.length} beats · {groups.length} {groups.length === 1 ? 'page' : 'pages'} ·
          page holds ≈ {Math.round(capacity)}w at this font
        </span>
        {!collapsed && (
          <span className="srail__hint">
            Click a gap between beats to add or remove a page turn.
          </span>
        )}
      </div>

      {!collapsed && (
        <div className="srail__strip" role="list">
          {groups.map((group, pageIdx) => {
            const words = group.reduce((s, i) => s + wordCounts[i], 0);
            const st = pageFillState(words, capacity);
            const isLastPage = pageIdx === groups.length - 1;
            return (
              <div className="srail__pagewrap" key={`page-${group[0]}`} role="listitem">
                <div className={`srail__page srail__page--${pageIdx % 2 ? 'odd' : 'even'}`}>
                  <button
                    type="button"
                    className="srail__pagehead"
                    onClick={() => jumpToPage(pageIdx)}
                    title={`Show spread ${Math.floor(pageIdx / 2) + 1}`}
                  >
                    <span className="srail__pagenum">P{pageIdx + 1}</span>
                    <span className={`srail__pill srail__pill--${st.kind}`}>
                      {st.kind === 'overflow'
                        ? `${Math.ceil(words - capacity)}w over`
                        : st.kind}
                    </span>
                    <span className="srail__pagewords">
                      {words}w
                      {st.kind !== 'overflow' && st.kind !== 'empty' && (
                        <> · art ≈ {Math.round(st.artShare * 100)}%</>
                      )}
                    </span>
                    <span className="srail__meter" aria-hidden>
                      <span
                        className={`srail__meter-fill srail__meter-fill--${st.kind}`}
                        style={{ width: `${Math.min(100, st.fill * 100)}%` }}
                      />
                    </span>
                  </button>
                  <div className="srail__beats">
                    {group.map((beatIdx, k) => (
                      <BeatCell
                        key={beats[beatIdx].id}
                        beat={beats[beatIdx]}
                        words={wordCounts[beatIdx]}
                        showGapAfter={k < group.length - 1}
                        gapIndex={beatIdx}
                        onToggleGap={toggleGap}
                        disabled={regrouping}
                      />
                    ))}
                  </div>
                </div>
                {!isLastPage && (
                  <button
                    type="button"
                    className="srail__gap srail__gap--turn"
                    onClick={() => toggleGap(group[group.length - 1])}
                    disabled={regrouping}
                    title="Remove this page turn (merge pages)"
                    aria-label={`Remove page turn after "${beats[group[group.length - 1]].title}"`}
                  >
                    <span aria-hidden>⤶</span>
                    <span className="srail__gap-label" aria-hidden>
                      merge
                    </span>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function BeatCell({
  beat,
  words,
  showGapAfter,
  gapIndex,
  onToggleGap,
  disabled,
}: {
  beat: StoryBeat;
  words: number;
  showGapAfter: boolean;
  gapIndex: number;
  onToggleGap: (gap: number) => void;
  disabled: boolean;
}) {
  return (
    <>
      <div
        className="srail__beat"
        style={{ flexGrow: Math.max(words, 4) }}
        title={beat.text_content}
      >
        <span className="srail__beat-title">{beat.title}</span>
        <span className="srail__beat-words">{words}w</span>
      </div>
      {showGapAfter && (
        <button
          type="button"
          className="srail__gap"
          onClick={() => onToggleGap(gapIndex)}
          disabled={disabled}
          title="Start a new page here"
          aria-label={`Add page turn after "${beat.title}"`}
        >
          <span className="srail__gap-label" aria-hidden>
            split
          </span>
        </button>
      )}
    </>
  );
}
